/**
 * Camada Meta (PLANO-DIFERENCIAIS Pilar 6): Daily/Streak, Collection Log, Missões,
 * Conquistas e Pass Points. Núcleo puro — sem DOM, sem I/O.
 */

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const DAY_MS = 86400000;

// O reset do produto é 04:00 em São Paulo (07:00 UTC). Igualar a função SQL
// `game_day_brt()` evita divergência cliente/servidor perto da virada.
const RESET_OFFSET_MS = 7 * 3600000;
const startOfDayBrt = (ts) => Math.floor((Number(ts) - RESET_OFFSET_MS) / DAY_MS) * DAY_MS + RESET_OFFSET_MS;

export function normalizeMeta(state) {
  if (!state || typeof state !== 'object') return state;
  const current = state.meta && typeof state.meta === 'object' ? state.meta : {};
  const log = current.collectionLog && typeof current.collectionLog === 'object' ? current.collectionLog : {};
  state.meta = {
    daily: {
      lastClaimAt: Math.max(0, Number(current.daily?.lastClaimAt) || 0),
      streak: Math.max(0, Math.floor(Number(current.daily?.streak) || 0)),
    },
    collectionLog: {
      shinies: Array.isArray(log.shinies) ? [...new Set(log.shinies)] : [],
      ivQuality: Array.isArray(log.ivQuality) ? [...new Set(log.ivQuality)] : [],
      fish: Array.isArray(log.fish) ? [...new Set(log.fish)] : [],
      crafts: Array.isArray(log.crafts) ? [...new Set(log.crafts)] : [],
      fossils: Array.isArray(log.fossils) ? [...new Set(log.fossils)] : [],
    },
    quests: {
      active: Array.isArray(current.quests?.active) ? current.quests.active : [],
      rotatedAt: Math.max(0, Number(current.quests?.rotatedAt) || 0),
      rotationKey: String(current.quests?.rotationKey || ''),
      dailyBonusClaimed: !!current.quests?.dailyBonusClaimed,
    },
    achievements: {
      unlocked: Array.isArray(current.achievements?.unlocked) ? [...new Set(current.achievements.unlocked)] : [],
    },
    passPoints: Math.max(0, Math.floor(Number(current.passPoints) || 0)),
  };
  return state;
}

/** Calendário de presentes diários: 60 dias, progressão suave, ciclo reinicia. */
export const DAILY_GIFT_DAYS = 60;

const dailyGift = (coins, items = null, extra = null) => Object.freeze({
  coins,
  ...(items ? { items: Object.freeze(items) } : {}),
  ...(extra || {}),
});

/**
 * Dia 1 → básico; dia 60 → melhor do ciclo (ainda sem quebrar economia).
 * Marcos leves a cada 7 dias; esmeraldas só em marcos maiores.
 */
export const DAILY_REWARDS = Object.freeze([
  // 1–10
  dailyGift(180, { pokeball: 3 }),
  dailyGift(200, { potion: 2 }),
  dailyGift(220, { pokeball: 4 }),
  dailyGift(250, { 'great-potion': 1 }),
  dailyGift(280, { greatball: 1 }),
  dailyGift(300, { pokeball: 5 }),
  dailyGift(350, { potion: 3 }, { boost: Object.freeze({ key: 'trainerXp', hours: 1 }) }),
  dailyGift(380, { greatball: 2 }),
  dailyGift(400, { revive: 1 }),
  dailyGift(450, { 'great-potion': 2 }),
  // 11–20
  dailyGift(480, { greatball: 2, potion: 2 }),
  dailyGift(520, { pokeball: 6 }),
  dailyGift(560, { superball: 1 }),
  dailyGift(600, { 'great-potion': 2 }),
  dailyGift(640, { greatball: 3 }),
  dailyGift(680, { revive: 1, potion: 2 }),
  dailyGift(720, { superball: 1 }, { boost: Object.freeze({ key: 'pokemonXp', hours: 1 }) }),
  dailyGift(760, { greatball: 3, 'great-potion': 1 }),
  dailyGift(800, { superball: 2 }),
  dailyGift(850, { revive: 1 }, { emerald: 1 }),
  // 21–30
  dailyGift(900, { greatball: 4 }),
  dailyGift(950, { 'great-potion': 3 }),
  dailyGift(1000, { superball: 2 }),
  dailyGift(1050, { revive: 2 }),
  dailyGift(1100, { ultraball: 1 }),
  dailyGift(1150, { superball: 2, potion: 3 }),
  dailyGift(1200, { 'great-potion': 3 }, { boost: Object.freeze({ key: 'loot', hours: 1 }) }),
  dailyGift(1250, { ultraball: 1, greatball: 2 }),
  dailyGift(1300, { revive: 2, superball: 1 }),
  dailyGift(1400, { ultraball: 1 }, { emerald: 1 }),
  // 31–40
  dailyGift(1450, { superball: 3 }),
  dailyGift(1500, { 'great-potion': 4 }),
  dailyGift(1550, { ultraball: 1, revive: 1 }),
  dailyGift(1600, { superball: 3, potion: 4 }),
  dailyGift(1650, { ultraball: 1 }, { boost: Object.freeze({ key: 'capture', hours: 1 }) }),
  dailyGift(1700, { revive: 2, 'great-potion': 2 }),
  dailyGift(1750, { ultraball: 2 }),
  dailyGift(1800, { superball: 4 }),
  dailyGift(1850, { revive: 2, greatball: 4 }),
  dailyGift(1950, { ultraball: 1 }, { emerald: 1 }),
  // 41–50
  dailyGift(2000, { 'great-potion': 4, superball: 2 }),
  dailyGift(2050, { ultraball: 2 }),
  dailyGift(2100, { revive: 3 }),
  dailyGift(2150, { ultraball: 1, superball: 3 }),
  dailyGift(2200, { 'ultra-potion': 2 }, { boost: Object.freeze({ key: 'shinyLure', hours: 1 }) }),
  dailyGift(2250, { ultraball: 2, revive: 1 }),
  dailyGift(2300, { superball: 4, 'great-potion': 3 }),
  dailyGift(2400, { ultraball: 2 }),
  dailyGift(2450, { revive: 3, 'ultra-potion': 1 }),
  dailyGift(2550, { ultraball: 2 }, { emerald: 2 }),
  // 51–60
  dailyGift(2600, { superball: 5, revive: 1 }),
  dailyGift(2650, { ultraball: 2, 'great-potion': 3 }),
  dailyGift(2700, { revive: 3, superball: 3 }),
  dailyGift(2750, { ultraball: 2 }, { boost: Object.freeze({ key: 'trainerXp', hours: 1 }) }),
  dailyGift(2800, { 'ultra-potion': 3, revive: 2 }),
  dailyGift(2850, { ultraball: 3 }),
  dailyGift(2900, { revive: 3, superball: 4 }),
  dailyGift(3000, { ultraball: 2 }, { emerald: 1 }),
  dailyGift(3100, { ultraball: 3, revive: 2 }),
  dailyGift(3200, { ultraball: 3, revive: 3 }, { emerald: 2 }),
]);

/** Decide whether the login flow should open the daily gift panel automatically. */
export function shouldShowDailyGiftOnLogin({ isAuthenticated = false, isFirstLogin = true, isPreview = false } = {}) {
  return !!isAuthenticated && !isFirstLogin && !isPreview;
}

/**
 * Estado visual/lógico do calendário diário.
 * `claimableDay` = dia do ciclo (1–60) disponível agora, ou null se já resgatou hoje.
 * A sequência é baseada na quantidade de presentes resgatados, não em dias consecutivos.
 */
export function dailyGiftProgress(state, now = Date.now()) {
  normalizeMeta(state);
  const { daily } = state.meta;
  const today = startOfDayBrt(now);
  const lastDay = daily.lastClaimAt ? startOfDayBrt(daily.lastClaimAt) : 0;
  const claimedToday = !!(daily.lastClaimAt && today === lastDay);
  const hasClaimed = daily.streak > 0;
  const claimedInCycle = hasClaimed ? ((daily.streak - 1) % DAILY_GIFT_DAYS) + 1 : 0;

  return {
    streak: daily.streak || 0,
    claimedToday,
    streakAlive: hasClaimed,
    claimedInCycle,
    claimableDay: claimedToday ? null : (daily.streak % DAILY_GIFT_DAYS) + 1,
    canClaim: !claimedToday,
  };
}

/**
 * Reivindica o presente diário. Há no máximo um claim por dia BRT (reset 04:00),
 * mas a sequência continua do próximo presente mesmo depois de qualquer ausência.
 * Retorno estável: `{ ok, error, streak, reward, day }`.
 */
export function claimDailyGift(state, now = Date.now()) {
  if (!state || typeof state !== 'object') return { ok: false, error: 'invalid_state' };
  normalizeMeta(state);
  const { daily } = state.meta;
  const today = startOfDayBrt(now);
  const lastDay = daily.lastClaimAt ? startOfDayBrt(daily.lastClaimAt) : 0;
  if (daily.lastClaimAt && today === lastDay) return { ok: false, error: 'already_claimed', streak: daily.streak };

  daily.streak += 1;
  daily.lastClaimAt = now;
  const day = ((daily.streak - 1) % DAILY_GIFT_DAYS) + 1;
  const reward = DAILY_REWARDS[day - 1];
  return { ok: true, streak: daily.streak, day, reward };
}

const LOG_CATEGORIES = Object.freeze(['shinies', 'ivQuality', 'fish', 'crafts', 'fossils']);

/** Registra uma entrada única no Collection Log. Retorna `isNew` para acionar toasts de marco. */
export function logCollectionEntry(state, category, key) {
  if (!state || typeof state !== 'object' || !LOG_CATEGORIES.includes(category) || !key) {
    return { ok: false, isNew: false };
  }
  normalizeMeta(state);
  const bucket = state.meta.collectionLog[category];
  if (bucket.includes(key)) return { ok: true, isNew: false };
  bucket.push(key);
  return { ok: true, isNew: true };
}

export const logShinyCapture = (state, speciesSlug) => logCollectionEntry(state, 'shinies', speciesSlug);
export const logIvQualityCapture = (state, qualityName) => logCollectionEntry(state, 'ivQuality', qualityName);
export const logFishCaught = (state, speciesSlug) => logCollectionEntry(state, 'fish', speciesSlug);
export const logCraftCompleted = (state, recipeId) => logCollectionEntry(state, 'crafts', recipeId);
export const logFossilRevival = (state, fossilSlug) => logCollectionEntry(state, 'fossils', fossilSlug);

/** Banco de 24 missões: uma de cada categoria é selecionada às 04:00 BRT. */
export const QUEST_TEMPLATES = Object.freeze([
  Object.freeze({ id: 'combat-1', category: 'combat', type: 'defeat', targetMin: 20, targetMax: 30, rewardCoinsPerUnit: 10 }),
  Object.freeze({ id: 'combat-2', category: 'combat', type: 'defeat', targetMin: 30, targetMax: 45, rewardCoinsPerUnit: 10 }),
  Object.freeze({ id: 'combat-3', category: 'combat', type: 'defeat', targetMin: 45, targetMax: 60, rewardCoinsPerUnit: 11 }),
  Object.freeze({ id: 'combat-4', category: 'combat', type: 'defeat', targetMin: 55, targetMax: 75, rewardCoinsPerUnit: 12 }),
  Object.freeze({ id: 'combat-5', category: 'combat', type: 'defeat', targetMin: 70, targetMax: 90, rewardCoinsPerUnit: 13 }),
  Object.freeze({ id: 'combat-6', category: 'combat', type: 'defeat', targetMin: 85, targetMax: 110, rewardCoinsPerUnit: 14 }),
  Object.freeze({ id: 'capture-1', category: 'capture', type: 'capture', targetMin: 3, targetMax: 5, rewardCoinsPerUnit: 80 }),
  Object.freeze({ id: 'capture-2', category: 'capture', type: 'capture', targetMin: 5, targetMax: 7, rewardCoinsPerUnit: 80 }),
  Object.freeze({ id: 'capture-3', category: 'capture', type: 'capture', targetMin: 6, targetMax: 9, rewardCoinsPerUnit: 90 }),
  Object.freeze({ id: 'capture-4', category: 'capture', type: 'capture', targetMin: 8, targetMax: 11, rewardCoinsPerUnit: 95 }),
  Object.freeze({ id: 'capture-5', category: 'capture', type: 'capture', targetMin: 10, targetMax: 14, rewardCoinsPerUnit: 100 }),
  Object.freeze({ id: 'capture-6', category: 'capture', type: 'capture', targetMin: 12, targetMax: 16, rewardCoinsPerUnit: 110 }),
  Object.freeze({ id: 'explore-1', category: 'explore', type: 'fish', targetMin: 3, targetMax: 5, rewardCoinsPerUnit: 70 }),
  Object.freeze({ id: 'explore-2', category: 'explore', type: 'fish', targetMin: 5, targetMax: 7, rewardCoinsPerUnit: 75 }),
  Object.freeze({ id: 'explore-3', category: 'explore', type: 'fish', targetMin: 6, targetMax: 9, rewardCoinsPerUnit: 80 }),
  Object.freeze({ id: 'explore-4', category: 'explore', type: 'fish', targetMin: 8, targetMax: 11, rewardCoinsPerUnit: 85 }),
  Object.freeze({ id: 'explore-5', category: 'explore', type: 'fish', targetMin: 10, targetMax: 13, rewardCoinsPerUnit: 90 }),
  Object.freeze({ id: 'explore-6', category: 'explore', type: 'fish', targetMin: 12, targetMax: 16, rewardCoinsPerUnit: 95 }),
  Object.freeze({ id: 'economy-1', category: 'economy', type: 'craft', targetMin: 2, targetMax: 3, rewardCoinsPerUnit: 180 }),
  Object.freeze({ id: 'economy-2', category: 'economy', type: 'craft', targetMin: 3, targetMax: 5, rewardCoinsPerUnit: 180 }),
  Object.freeze({ id: 'economy-3', category: 'economy', type: 'craft', targetMin: 5, targetMax: 7, rewardCoinsPerUnit: 190 }),
  Object.freeze({ id: 'economy-4', category: 'economy', type: 'sell', targetMin: 10, targetMax: 20, rewardCoinsPerUnit: 22 }),
  Object.freeze({ id: 'economy-5', category: 'economy', type: 'sell', targetMin: 20, targetMax: 35, rewardCoinsPerUnit: 20 }),
  Object.freeze({ id: 'economy-6', category: 'economy', type: 'sell', targetMin: 35, targetMax: 55, rewardCoinsPerUnit: 18 }),
]);

const QUEST_RESET_UTC_OFFSET_MS = 7 * 3600000;
export const questRotationKey = (now = Date.now()) => String(Math.floor((Number(now) - QUEST_RESET_UTC_OFFSET_MS) / DAY_MS));

export function questsNeedRotation(state, now = Date.now()) {
  normalizeMeta(state);
  return state.meta.quests.rotationKey !== questRotationKey(now) || state.meta.quests.active.length !== 4;
}

/** Seleciona quatro missões: combate, captura, exploração e economia. */
export function rotateQuests(state, now = Date.now(), random = Math.random) {
  if (!state || typeof state !== 'object') return [];
  normalizeMeta(state);
  const categories = ['combat', 'capture', 'explore', 'economy'];
  const active = categories.map((category) => {
    const pool = QUEST_TEMPLATES.filter((template) => template.category === category);
    const template = pool[Math.floor(clamp(Number(random()) || 0, 0, 0.999999) * pool.length)];
    const span = template.targetMax - template.targetMin;
    const target = template.targetMin + Math.round(clamp(Number(random()) || 0, 0, 0.999999) * span);
    return {
      id: template.id, category, type: template.type, target, progress: 0, completed: false, claimed: false,
    };
  });
  state.meta.quests = { active, rotatedAt: now, rotationKey: questRotationKey(now), dailyBonusClaimed: false };
  return active;
}

/** Soma progresso de todas as missões ativas do tipo informado (auto-track). */
export function progressQuest(state, type, amount = 1) {
  if (!state || typeof state !== 'object' || amount <= 0) return [];
  normalizeMeta(state);
  const touched = [];
  for (const quest of state.meta.quests.active) {
    if (quest.type !== type || quest.completed) continue;
    quest.progress = Math.min(quest.target, quest.progress + amount);
    if (quest.progress >= quest.target) quest.completed = true;
    touched.push(quest.id);
  }
  return touched;
}

/** Reivindica a recompensa de uma missão concluída. Retorno estável para a UI. */
export function claimQuest(state, questId) {
  if (!state || typeof state !== 'object') return { ok: false, error: 'invalid_state' };
  normalizeMeta(state);
  const quest = state.meta.quests.active.find((entry) => entry.id === questId);
  const template = QUEST_TEMPLATES.find((entry) => entry.id === questId);
  if (!quest || !template) return { ok: false, error: 'quest_not_found' };
  if (!quest.completed) return { ok: false, error: 'not_completed' };
  if (quest.claimed) return { ok: false, error: 'already_claimed' };
  quest.claimed = true;
  const allClaimed = state.meta.quests.active.length === 4 && state.meta.quests.active.every((entry) => entry.claimed);
  const completionBonus = allClaimed && !state.meta.quests.dailyBonusClaimed ? 20 : 0;
  if (completionBonus) state.meta.quests.dailyBonusClaimed = true;
  return {
    ok: true,
    rewardPassPoints: 15 + completionBonus,
    completionBonus,
    rewardCoins: template.rewardCoinsPerUnit * quest.target,
  };
}

/** Marcos únicos com checagem pura sobre o `state`. */
export const ACHIEVEMENTS = Object.freeze([
  Object.freeze({
    id: 'first-shiny', name: 'Primeiro Brilho', rewardCoins: 5000,
    check: (state) => (state?.meta?.collectionLog?.shinies?.length || 0) >= 1,
  }),
  Object.freeze({
    id: 'first-craft', name: 'Primeira Fabricação', rewardCoins: 2000,
    check: (state) => (state?.meta?.collectionLog?.crafts?.length || 0) >= 1,
  }),
  Object.freeze({
    id: 'hundred-captures', name: 'Centena de Capturas', rewardCoins: 8000,
    check: (state) => (Number(state?.stats?.captured) || 0) >= 100,
  }),
  Object.freeze({
    id: 'perfect-iv', name: 'Exemplar Perfeito', rewardCoins: 15000,
    check: (state) => (state?.meta?.collectionLog?.ivQuality || []).includes('perfeito'),
  }),
  Object.freeze({
    id: 'first-fossil', name: 'Paleontólogo', rewardCoins: 6000,
    check: (state) => (state?.meta?.collectionLog?.fossils?.length || 0) >= 1,
  }),
  Object.freeze({
    id: 'champion', name: 'Campeão', rewardCoins: 20000,
    check: (state) => !!state?.eliteProgress?.championDefeated,
  }),
]);

/** Avalia todas as conquistas pendentes e desbloqueia as que passarem no `check`. Retorna as novas. */
export function checkAchievements(state) {
  if (!state || typeof state !== 'object') return [];
  normalizeMeta(state);
  const unlocked = state.meta.achievements.unlocked;
  const newlyUnlocked = [];
  for (const achievement of ACHIEVEMENTS) {
    if (unlocked.includes(achievement.id)) continue;
    if (!achievement.check(state)) continue;
    unlocked.push(achievement.id);
    newlyUnlocked.push(achievement);
  }
  return newlyUnlocked;
}

export function grantPassPoints(state, amount, reason = 'gameplay') {
  if (!state || typeof state !== 'object') return 0;
  normalizeMeta(state);
  const delta = Math.max(0, Math.floor(Number(amount) || 0));
  if (delta) state.meta.passPoints += delta;
  void reason; // reservado para telemetria futura, sem custo de lógica hoje
  return state.meta.passPoints;
}
